import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import { authInstance } from '../..';
import {
  fetchCommentsForObservation,
  postComment,
  patchComment,
  deleteCommentApi,
} from '../../store/api';
import {
  receiveCommentsForFunction,
  commentAdded,
  commentUpdated,
  commentRemoved,
  markFunctionRead,
} from '../../store/comments/commentsActions';
import { getCommentsForFunction } from '../../store/comments/commentsSelectors';
import { getCauses } from '../../store/graph/graphSelectors';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background-color: var(--color-dark);
  border-radius: 10px;
  width: 100%;
  height: 100%;
  min-height: 0;
`;

const ThreadList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const Empty = styled.div`
  color: var(--color-charcoal-light);
  font-size: 0.85rem;
  // font-style: italic;
  padding: 8px 0;
  text-align: center;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px;
  background-color: var(--color-dark-shade);
  border-radius: 6px;
`;

const ItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  color: var(--color-gray);
`;

const Author = styled.span`
  font-weight: bold;
  color: ${(props) => (props.role === 'automation' ? 'var(--color-green)' : 'var(--color-white)')};
`;

const TypeTag = styled.span`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 0.65rem;
  text-transform: uppercase;
  font-weight: bold;
  background-color: ${(props) => {
    switch (props.type) {
      case 'action_taken':
        return 'var(--color-green)';
      case 'important':
        return 'var(--color-observations)';
      case 'unimportant':
        return 'var(--color-charcoal-light)';
      default:
        return 'var(--color-charcoal)';
    }
  }};
  color: ${(props) =>
    props.type === 'action_taken' || props.type === 'important'
      ? 'var(--color-charcoal)'
      : 'var(--color-white)'};
`;

const ItemBody = styled.div`
  font-size: 0.85rem;
  color: var(--color-white);
  white-space: pre-wrap;
  word-break: break-word;
`;

const ItemActions = styled.div`
  display: flex;
  gap: 6px;
  margin-left: auto;
  font-size: 0.7rem;
`;

const ActionLink = styled.button`
  background: none;
  color: var(--color-charcoal-light);
  font-size: 0.7rem;
  padding: 0;
  cursor: pointer;
  &:hover {
    color: var(--color-white);
  }
`;

const Compose = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  margin-top: 8px;
  border-top: 1px solid var(--color-charcoal);
`;

const QuickTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const TagButton = styled.button`
  padding: 4px 10px;
  border-radius: 12px;
  border: 1px solid var(--color-charcoal-light);
  background-color: transparent;
  color: var(--color-white);
  font-size: 0.7rem;
  cursor: pointer;
  &:hover {
    border-color: var(--color-observations);
    color: var(--color-observations);
  }
  &[data-selected='true'] {
    background-color: var(--color-observations);
    color: var(--color-charcoal);
    border-color: var(--color-observations);
  }
`;

const TextRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: stretch;
`;

const TextInput = styled.textarea`
  flex: 1;
  min-height: 36px;
  max-height: 100px;
  resize: vertical;
  padding: 6px 8px;
  border-radius: 6px;
  background-color: var(--color-dark-shade);
  color: var(--color-white);
  border: 1px solid var(--color-charcoal-light);
  font-family: inherit;
  font-size: 0.85rem;
  &:focus {
    outline: none;
    border-color: var(--color-observations);
  }
`;

const SubmitButton = styled.button`
  padding: 6px 14px;
  border-radius: 6px;
  background-color: var(--color-observations);
  color: var(--color-charcoal);
  font-weight: bold;
  font-size: 0.8rem;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CharCount = styled.div`
  font-size: 0.7rem;
  color: var(--color-charcoal-light);
  text-align: right;
`;

const TEXT_LIMIT = 2000;

const QUICK_TYPES = [
  { value: 'action_taken', label: 'Action taken' },
  { value: 'important', label: 'Important' },
  { value: 'unimportant', label: 'Unimportant' },
  { value: 'note', label: 'Note' },
];

const formatTime = (iso) => {
  const dt = DateTime.fromISO(iso);
  const diff = DateTime.utc().diff(dt, ['minutes', 'hours', 'days']).toObject();
  if (diff.days >= 1) return dt.toFormat('LLL d, HH:mm');
  if (diff.hours >= 1) return `${Math.floor(diff.hours)}h ago`;
  if (diff.minutes >= 1) return `${Math.floor(diff.minutes)}m ago`;
  return 'just now';
};

const CommentThread = ({
  functionId,
  observationLabel,
  observationDescription,
  observationFault,
}) => {
  const dispatch = useDispatch();
  const comments = useSelector(getCommentsForFunction(functionId));
  const causes = useSelector(getCauses);

  const [type, setType] = useState('note');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);

  const currentUser = authInstance?.tokenParsed?.preferred_username;

  const snapshotCauses = useMemo(
    () =>
      (causes || [])
        .filter(
          (c) =>
            Array.isArray(c.observationFunctions) && c.observationFunctions.includes(functionId)
        )
        .map((c) => ({ id: c.functionId, label: c.label || '' })),
    [causes, functionId]
  );

  useEffect(() => {
    let cancelled = false;
    fetchCommentsForObservation(functionId)
      .then(({ response }) => {
        if (cancelled || !response) return;
        const list = response.comments || [];
        dispatch(receiveCommentsForFunction({ functionId, comments: list }));
        if (list.length > 0) {
          const latest = list.reduce(
            (max, c) => (max == null || c.createdAt > max ? c.createdAt : max),
            null
          );
          dispatch(markFunctionRead({ functionId, timestamp: latest }));
        }
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [functionId, dispatch]);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    if (!text && type === 'note') return;
    setSubmitting(true);
    setError(null);
    try {
      if (editingId) {
        const { response, error: err } = await patchComment(functionId, editingId, { type, text });
        if (err) throw new Error(err);
        if (response) dispatch(commentUpdated(response));
        setEditingId(null);
      } else {
        const body = {
          type,
          text: text || null,
          snapshotLabel: observationLabel || null,
          snapshotDescription: observationDescription || null,
          snapshotFault: observationFault || null,
          snapshotCauses,
        };
        const { response, error: err } = await postComment(functionId, body);
        if (err) throw new Error(err);
        if (response) {
          dispatch(commentAdded(response));
          dispatch(markFunctionRead({ functionId, timestamp: response.createdAt }));
        }
      }
      setText('');
      setType('note');
    } catch (err) {
      setError(err.message || 'Failed to save comment');
    } finally {
      setSubmitting(false);
    }
  }, [
    submitting,
    text,
    type,
    editingId,
    functionId,
    observationLabel,
    observationDescription,
    observationFault,
    snapshotCauses,
    dispatch,
  ]);

  const handleStartEdit = (comment) => {
    setEditingId(comment.commentId);
    setType(comment.type);
    setText(comment.text || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setType('note');
    setText('');
  };

  const handleDelete = async (comment) => {
    const { error: err } = await deleteCommentApi(functionId, comment.commentId);
    if (err) {
      setError(err);
      return;
    }
    dispatch(commentRemoved({ functionId, commentId: comment.commentId }));
  };

  return (
    <Wrapper onClick={(e) => e.stopPropagation()}>
      <ThreadList>
        {comments.length === 0 ? (
          <Empty>No comments yet...</Empty>
        ) : (
          comments.map((c) => (
            <Item key={c.commentId}>
              <ItemHeader>
                <Author role={c.authorRole}>{c.author}</Author>
                {c.type && c.type !== 'note' && (
                  <TypeTag type={c.type}>{c.type.replace('_', ' ')}</TypeTag>
                )}
                <span>
                  {formatTime(c.createdAt)}
                  {c.editedAt ? ' (edited)' : ''}
                </span>
                {c.author === currentUser && (
                  <ItemActions>
                    <ActionLink type="button" onClick={() => handleStartEdit(c)}>
                      edit
                    </ActionLink>
                    <ActionLink type="button" onClick={() => handleDelete(c)}>
                      delete
                    </ActionLink>
                  </ItemActions>
                )}
              </ItemHeader>
              {c.text && <ItemBody>{c.text}</ItemBody>}
            </Item>
          ))
        )}
      </ThreadList>

      <Compose>
        <QuickTags>
          {QUICK_TYPES.map((qt) => (
            <TagButton
              key={qt.value}
              data-selected={type === qt.value}
              onClick={() => setType(qt.value)}
              type="button"
            >
              {qt.label}
            </TagButton>
          ))}
        </QuickTags>
        <TextRow>
          <TextInput
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, TEXT_LIMIT))}
            placeholder={
              editingId
                ? 'Edit your comment...'
                : type === 'note'
                ? 'Write a comment...'
                : 'Add an optional note...'
            }
          />
          <SubmitButton
            type="button"
            onClick={handleSubmit}
            disabled={submitting || (type === 'note' && !text.trim())}
          >
            {editingId ? 'Save' : 'Post'}
          </SubmitButton>
        </TextRow>
        {editingId && (
          <ActionLink type="button" onClick={handleCancelEdit}>
            cancel edit
          </ActionLink>
        )}
        {text && (
          <CharCount>
            {text.length}/{TEXT_LIMIT}
          </CharCount>
        )}
        {error && <CharCount style={{ color: 'tomato', textAlign: 'left' }}>{error}</CharCount>}
      </Compose>
    </Wrapper>
  );
};

export default CommentThread;
